import { useEffect, useState } from "react";
import { Check, Copy, Maximize2, Minimize2 } from "lucide-react";
import { Button, cn } from "@atrix-analytics/ui";

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Copies `value`; announces success politely for screen readers. */
export function CopyButton({
  value,
  label = "Copy",
  className,
  showLabel = false,
}: {
  value: string;
  label?: string;
  className?: string;
  showLabel?: boolean;
}) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  useEffect(() => {
    if (state === "idle") return;
    const t = setTimeout(() => setState("idle"), 1600);
    return () => clearTimeout(t);
  }, [state]);

  return (
    <Button
      type="button"
      variant="secondary"
      size={showLabel ? "sm" : "icon"}
      className={cn(!showLabel && "size-7", className)}
      onClick={async () => setState((await copyText(value)) ? "copied" : "failed")}
      aria-label={showLabel ? undefined : label}
    >
      {state === "copied" ? <Check className="text-ok" /> : <Copy />}
      {showLabel && <span>{state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : label}</span>}
      <span className="sr-only" aria-live="polite">
        {state === "copied" ? "Copied to clipboard" : state === "failed" ? "Couldn't copy — select the text instead" : ""}
      </span>
    </Button>
  );
}

/**
 * A monospace value with a copy button — keys, tokens, snippets. With `expandable`, a toggle shows
 * the whole value wrapped instead of truncated, so it can be read and checked character by character.
 */
export function CopyField({
  value,
  label,
  className,
  expandable = false,
}: {
  value: string;
  label: string;
  className?: string;
  expandable?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className={cn(
        "flex min-w-0 gap-2 rounded-md border border-border-strong bg-bg-inset py-1 pr-1 pl-3",
        expanded ? "items-start" : "items-center",
        className,
      )}
    >
      <code
        className={cn("min-w-0 flex-1 font-mono text-[12.5px] text-fg", expanded ? "py-1 break-all" : "truncate")}
        title={expanded ? undefined : value}
      >
        {value}
      </code>
      {expandable && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={expanded ? `Shorten ${label}` : `Show full ${label}`}
          aria-expanded={expanded}
          onClick={() => setExpanded((e) => !e)}
        >
          {expanded ? <Minimize2 /> : <Maximize2 />}
        </Button>
      )}
      <CopyButton value={value} label={`Copy ${label}`} />
    </div>
  );
}
